import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Card,
  CardHeader,
  CardContent,
  Divider,
  TablePagination,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PersonIcon from '@mui/icons-material/Person';
import * as adminService from '../services/adminService';

const PractitionerAppointmentTypeManager = ({ clinic }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [practitioners, setPractitioners] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [debugInfo, setDebugInfo] = useState(null);

  // Load all data when component mounts or clinic changes
  useEffect(() => {
    if (clinic) {
      console.log('Loading data for clinic ID:', clinic);
      loadData();
    }
  }, [clinic]);

  const loadData = async () => {
    setLoading(true);
    
    try {
      // Get the clinic ID - it might be passed as an object or a direct number
      const clinicId = typeof clinic === 'object' ? clinic.id : clinic;
      console.log('Loading data with clinic ID:', clinicId);
      
      if (!clinicId) {
        console.error('No clinic ID provided');
        setSnackbar({
          open: true,
          message: 'Error: No clinic selected',
          severity: 'error'
        });
        setLoading(false);
        return;
      }
      
      // Load practitioners for this clinic
      console.log(`Fetching practitioners for clinic ${clinicId}...`);
      const practitionersResponse = await adminService.getPractitioners(clinicId);
      console.log('Practitioners loaded:', practitionersResponse);
      
      if (!practitionersResponse || !Array.isArray(practitionersResponse)) {
        console.error('Invalid practitioners response:', practitionersResponse);
        setSnackbar({
          open: true,
          message: 'Error: Failed to load practitioners',
          severity: 'error'
        });
        setLoading(false);
        return;
      }
      
      // Load appointment types for this clinic
      console.log(`Fetching appointment types for clinic ${clinicId}...`);
      const appointmentTypesResponse = await adminService.getClinicAppointmentTypes(clinicId);
      console.log('Appointment types loaded:', appointmentTypesResponse);
      
      if (!appointmentTypesResponse || !Array.isArray(appointmentTypesResponse)) {
        console.error('Invalid appointment types response:', appointmentTypesResponse);
        setSnackbar({
          open: true,
          message: 'Error: Failed to load appointment types',
          severity: 'error'
        });
        setLoading(false);
        return;
      }
      
      // Filter to only show enabled appointment types
      // Default to empty array if response is not in expected format
      let enabledAppointmentTypes = [];
      if (Array.isArray(appointmentTypesResponse)) {
        // Check both 'enabled' and 'is_enabled' fields for compatibility
        enabledAppointmentTypes = appointmentTypesResponse.filter(at => at.enabled || at.is_enabled);
        console.log(`Found ${enabledAppointmentTypes.length} enabled appointment types out of ${appointmentTypesResponse.length} total`);
        
        // Dump first few appointment types for debugging
        console.log('Sample appointment types:', appointmentTypesResponse.slice(0, 3));
      } else {
        console.error('Unexpected appointment types response format:', appointmentTypesResponse);
        // Try to extract data if it's wrapped in another object
        if (appointmentTypesResponse && Array.isArray(appointmentTypesResponse.appointmentTypes)) {
          enabledAppointmentTypes = appointmentTypesResponse.appointmentTypes.filter(at => at.enabled || at.is_enabled);
          console.log(`Found ${enabledAppointmentTypes.length} enabled appointment types after unwrapping`);
        }
      }
      
      // Initialize assignments object
      const initialAssignments = {};
      
      // Populate practitioners and their assignments
      console.log('Starting to fetch assignments for each practitioner...');
      
      for (const practitioner of practitionersResponse) {
        console.log(`Processing practitioner: ${practitioner.id} - ${practitioner.name}`);
        
        try {
          // Get assignments for this practitioner
          console.log(`Fetching assignments for practitioner ${practitioner.id} and clinic ${clinicId}`);
          const practitionerAssignments = await adminService.getPractitionerAppointmentTypes(
            practitioner.id,
            clinicId
          );
          
          console.log(`Received ${practitionerAssignments ? practitionerAssignments.length : 0} assignments for practitioner ${practitioner.id}:`, practitionerAssignments);
          
          // Create map of appointment type IDs to boolean values
          initialAssignments[practitioner.id] = {};
          
          enabledAppointmentTypes.forEach(type => {
            const isAssigned = practitionerAssignments && 
              Array.isArray(practitionerAssignments) && 
              practitionerAssignments.some(a => a.id === type.id);
              
            console.log(`Appointment type ${type.id} (${type.name}) is ${isAssigned ? '' : 'not '}assigned to practitioner ${practitioner.id}`);
            
            initialAssignments[practitioner.id][type.id] = isAssigned;
          });
        } catch (error) {
          console.error(`Error processing assignments for practitioner ${practitioner.id}:`, error);
          // Initialize as empty to prevent errors
          initialAssignments[practitioner.id] = {};
          enabledAppointmentTypes.forEach(type => {
            initialAssignments[practitioner.id][type.id] = false;
          });
        }
      }
      
      console.log('Completed assignment processing. Final assignments:', initialAssignments);
      
      setPractitioners(practitionersResponse);
      setAppointmentTypes(enabledAppointmentTypes);
      setAssignments(initialAssignments);
    } catch (error) {
      console.error('Error loading data:', error);
      
      // Extract the most relevant error message
      let errorMessage = 'Error loading data. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = `Server error: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      // Reset state to prevent UI from freezing
      setPractitioners([]);
      setAppointmentTypes([]);
      setAssignments({});
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAssignment = (practitionerId, appointmentTypeId) => {
    // Create a deep copy of the assignments object
    const newAssignments = { ...assignments };
    
    // If the practitioner doesn't have an entry yet, create one
    if (!newAssignments[practitionerId]) {
      newAssignments[practitionerId] = {};
    }
    
    // Toggle the assignment
    newAssignments[practitionerId][appointmentTypeId] = 
      !newAssignments[practitionerId][appointmentTypeId];
    
    console.log(`Toggle assignment for practitioner ${practitionerId}, appointment type ${appointmentTypeId} to:`, 
                newAssignments[practitionerId][appointmentTypeId]);
    
    setAssignments(newAssignments);
  };

  const handleSavePractitionerAssignments = async (practitionerId) => {
    try {
      setSaving(true);
      
      // Get all appointment type IDs that are assigned to this practitioner
      const appointmentTypeIds = [];
      for (const typeId in assignments[practitionerId]) {
        if (assignments[practitionerId][typeId]) {
          appointmentTypeIds.push(parseInt(typeId));
        }
      }
      
      // Get the clinic ID - it might be passed as an object or a direct number
      const clinicId = typeof clinic === 'object' ? clinic.id : clinic;
      
      // Save the assignments
      await adminService.assignAppointmentTypesToPractitioner(
        practitionerId,
        clinicId,
        appointmentTypeIds
      );
      
      setSnackbar({
        open: true,
        message: 'Assignments saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving assignments:', error);
      setSnackbar({
        open: true,
        message: 'Error saving assignments. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!clinic) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Practitioner Service Assignments" />
        <Divider />
        <CardContent>
          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => window.history.back()}
              >
                Back to Clinics
              </Button>
            }
          >
            Please select a clinic first to manage practitioner service assignments.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Calculate pagination
  const paginatedPractitioners = practitioners.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MedicalServicesIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Practitioner Service Assignments {clinic ? `- Clinic ID: ${clinic}` : ''}
              </Typography>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {loading && <CircularProgress size={24} sx={{ mr: 1 }} />}
              <Tooltip title="Refresh data">
                <IconButton onClick={loadData} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Assign which services (appointment types) each practitioner can provide. Only practitioners 
            assigned to a service will be shown to users when that service is selected during booking.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Note:</strong> Only enabled appointment types are shown below.
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : practitioners.length === 0 ? (
            <>
              <Alert severity="info" sx={{ mt: 2, mb: 2 }} action={
                <Button
                  color="primary"
                  size="small"
                  onClick={() => window.location.href = '/admin'}
                >
                  Back to Clinics
                </Button>
              }>
                No practitioners found for this clinic. Please add practitioners to the clinic first in Cliniko.
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Troubleshooting:</strong> Make sure this clinic has practitioners assigned in the clinic settings.
              </Typography>
            </>
          ) : appointmentTypes.length === 0 ? (
            <>
              <Alert severity="info" sx={{ mt: 2, mb: 2 }} action={
                <Button
                  color="primary"
                  size="small"
                  onClick={() => window.location.href = '/admin'}
                >
                  Back to Clinics
                </Button>
              }>
                No enabled appointment types found for this clinic. Please enable appointment types first.
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Note:</strong> You need to first enable appointment types for this clinic in the Clinic Management page. 
                Go back to Clinics, select this clinic, and click "Manage Appointment Types".
              </Typography>
            </>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '30%', fontWeight: 'bold' }}>
                        Practitioner
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Assigned Services
                      </TableCell>
                      <TableCell align="right" sx={{ width: '100px' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPractitioners.map((practitioner) => {
                      // Count how many services are assigned to this practitioner
                      const assignedCount = Object.values(assignments[practitioner.id] || {})
                        .filter(Boolean).length;
                      
                      return (
                        <TableRow key={practitioner.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {practitioner.photo_url ? (
                                <Box
                                  component="img"
                                  src={practitioner.photo_url}
                                  alt={practitioner.name}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    mr: 1
                                  }}
                                  onError={(e) => {
                                    e.target.onError = null;
                                    e.target.src = 'https://via.placeholder.com/40?text=P';
                                  }}
                                />
                              ) : (
                                <PersonIcon sx={{ fontSize: 40, mr: 1, color: 'grey.400' }} />
                              )}
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {practitioner.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {practitioner.id.substring(Math.max(0, practitioner.id.length - 6))}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {appointmentTypes.map((type) => (
                                <Chip
                                  key={type.id}
                                  label={type.name}
                                  size="small"
                                  color={assignments[practitioner.id]?.[type.id] ? "primary" : "default"}
                                  variant={assignments[practitioner.id]?.[type.id] ? "filled" : "outlined"}
                                  onClick={() => handleToggleAssignment(practitioner.id, type.id)}
                                  sx={{ mb: 0.5 }}
                                />
                              ))}
                              
                              {appointmentTypes.length > 0 && assignedCount === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No services assigned
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              startIcon={<SaveIcon />}
                              color="primary"
                              size="small"
                              variant="contained"
                              onClick={() => handleSavePractitionerAssignments(practitioner.id)}
                              disabled={saving}
                            >
                              Save
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={practitioners.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </CardContent>
      </Card>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PractitionerAppointmentTypeManager;